"use client";

import { useMemo, useState } from "react";
import { Sparkles, ArrowRight } from "lucide-react";

interface Brand {
  id: string;
  name: string;
  logo: string;
  category: string;
  discount?: number;
}

const brands: Brand[] = [
  // Shopping
  {
    id: "1",
    name: "Shopping",
    logo: "https://res.cloudinary.com/ole/image/upload/v1741976332/Going-Out_1_stuvhj.jpg",
    category: "Shopping",
  },
  {
    id: "2",
    name: "Max Fashion",
    logo: "https://res.cloudinary.com/ole/image/upload/v1710390488/Logo_of_Max_Fashion_and_Accessories__March_2018.png",
    category: "Shopping",
  },
  {
    id: "3",
    name: "Mothercare",
    logo: "https://res.cloudinary.com/ole/image/upload/v1710390487/mothercare1799.jpg",
    category: "Shopping",
  },
  {
    id: "4",
    name: "Wakefit",
    logo: "https://res.cloudinary.com/ole/image/upload/v1709964791/wakefit_home_thumb.png",
    category: "Shopping",
  },
  {
    id: "5",
    name: "Puma",
    logo: "https://res.cloudinary.com/ole/image/upload/v1709964526/Puma-Logo-Black-and-White.png",
    category: "Shopping",
  },
  {
    id: "6",
    name: "Lifestyle",
    logo: "https://res.cloudinary.com/ole/image/upload/v1709291713/lifestyle.png",
    category: "Shopping",
  },
  {
    id: "7",
    name: "Levis",
    logo: "https://res.cloudinary.com/ole/image/upload/v1707243128/png-transparent-hoodie-levi-strauss-co-jeans-levi-s-501-brand-logos-marcas-hoodie-levi-strauss-amp.png",
    category: "Shopping",
  },
  {
    id: "8",
    name: "Peter England",
    logo: "https://res.cloudinary.com/ole/image/upload/v1707229878/peter-england-logo-CAA89E9287-seeklogo.com.png",
    category: "Shopping",
  },
  // Travel
  {
    id: "9",
    name: "MakeMyTrip",
    logo: "https://res.cloudinary.com/ole/image/upload/v1724737296/makemytrip.png",
    category: "Travel",
  },
  {
    id: "10",
    name: "Yatra",
    logo: "https://res.cloudinary.com/ole/image/upload/v1724737218/yatra.png",
    category: "Travel",
  },
  {
    id: "11",
    name: "Taj Hotels",
    logo: "https://res.cloudinary.com/ole/image/upload/v1707280827/png-transparent-taj-falaknuma-palace-taj-hotels-resorts-and-palaces-brand-hotel-angle-text-logo-thumbnail.png",
    category: "Travel",
  },
  {
    id: "12",
    name: "ITC Hotels",
    logo: "https://res.cloudinary.com/ole/image/upload/v1707242608/iTsvMiYd_400x400.jpg",
    category: "Travel",
  },
  {
    id: "13",
    name: "Thomas Cook",
    logo: "https://res.cloudinary.com/ole/image/upload/v1707243127/download_4.png",
    category: "Travel",
  },
  {
    id: "14",
    name: "Uber",
    logo: "https://res.cloudinary.com/ole/image/upload/v1707283311/download.png",
    category: "Travel",
  },
  // Food
  {
    id: "15",
    name: "KFC",
    logo: "https://res.cloudinary.com/ole/image/upload/v1707038605/kisspng-colonel-sanders-kfc-fried-chicken-logo-restaurant-fried-chicken-5ab4d4671d4b30.22577779152180029512.jpg",
    category: "Food",
  },
  {
    id: "16",
    name: "AB's",
    logo: "https://res.cloudinary.com/ole/image/upload/v1707038605/voN2Zaw3_400x400.png",
    category: "Food",
  },
  // Groceries
  {
    id: "17",
    name: "Bigbasket",
    logo: "https://res.cloudinary.com/ole/image/upload/v1707019323/Bigbasket-800x400.jpg",
    category: "Groceries",
  },
  {
    id: "18",
    name: "Ratnadeep",
    logo: "https://res.cloudinary.com/ole/image/upload/v1707020020/ratnadeepdec252020kjghty_OERXaQn.jpg",
    category: "Groceries",
  },
  {
    id: "19",
    name: "Reliance Smart",
    logo: "https://res.cloudinary.com/ole/image/upload/v1707022922/9715_44841_Reliance-SMART_Point.png",
    category: "Groceries",
  },
  {
    id: "20",
    name: "Lulu",
    logo: "https://res.cloudinary.com/ole/image/upload/v1707023037/Logo-lulu-saudi-hypermarket-free-download-PNG.png",
    category: "Groceries",
  },
];

function BrandCard({
  name,
  logo,
  discount,
}: {
  name: string;
  logo: string;
  discount: number;
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="group relative overflow-hidden rounded-lg bg-slate-900/50 border border-slate-800 hover:border-cyan-500/50 transition-all duration-300 cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background Gradient on Hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 via-blue-500/0 to-slate-900/0 group-hover:from-cyan-500/10 group-hover:via-blue-500/5 group-hover:to-slate-900/10 transition-all duration-300" />

      {/* Content */}
      <div className="relative p-4 sm:p-6 flex flex-col items-center justify-center h-48 sm:h-56">
        {/* Logo Container */}
        <div className="relative w-full h-20 sm:h-24 mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
          <img
            src={logo || "/placeholder.svg"}
            alt={name}
            className="object-contain w-full h-full"
          />
        </div>

        {/* Brand Name */}
        <h3 className="text-center text-xs sm:text-sm font-medium text-slate-200 mb-3 line-clamp-2">
          {name}
        </h3>

        {/* Discount Badge */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full blur opacity-75 group-hover:opacity-100 transition-opacity" />
          <div className="relative bg-slate-950 px-4 sm:px-5 py-1.5 sm:py-2 rounded-full">
            <span className="text-base sm:text-lg font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              {discount}% OFF
            </span>
          </div>
        </div>
      </div>

      {/* Hover Arrow Indicator */}
      {isHovered && (
        <div className="absolute bottom-3 right-3 bg-cyan-500 p-2 rounded-full animate-pulse">
          <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
        </div>
      )}

      {/* Border Glow Effect */}
      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-cyan-500/0 via-cyan-500/0 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </div>
  );
}

export function PartnerBrands() {
  const brandsWithDiscounts = useMemo(() => {
    return brands.map((brand) => ({
      ...brand,
      discount: Math.floor(Math.random() * (25 - 5 + 1)) + 5,
    }));
  }, []);

  const categories = Array.from(new Set(brands.map((b) => b.category)));
  const groupedBrands = categories.reduce((acc, cat) => {
    acc[cat] = brandsWithDiscounts.filter((b) => b.category === cat);
    return acc;
  }, {} as Record<string, typeof brandsWithDiscounts>);

  return (
    <div className="min-h-screen bg-slate-950 pb-12">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-b from-slate-950 via-slate-950 to-slate-950/0 px-4 sm:px-6 lg:px-8 pt-8 pb-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="w-6 h-6 text-cyan-400" />
            <h1 className="text-3xl sm:text-4xl font-bold text-white">
              Exclusive Partner Benefits
            </h1>
          </div>
          <p className="text-slate-400 text-base sm:text-lg max-w-2xl">
            Shop with our partner brands to unlock extra discounts and maximize
            your savings with every purchase
          </p>
        </div>
      </div>

      {/* Categories Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {categories.map((category) => (
          <div key={category} className="mb-12">
            {/* Category Title */}
            <h2 className="text-xl sm:text-2xl font-semibold text-white mb-6 capitalize flex items-center gap-2">
              <div className="w-1 h-6 bg-gradient-to-b from-cyan-400 to-blue-500 rounded" />
              {category}
            </h2>

            {/* Brand Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {groupedBrands[category].map((brand) => (
                <BrandCard
                  key={brand.id}
                  name={brand.name}
                  logo={brand.logo}
                  discount={brand.discount!}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer CTA */}
      <div className="mt-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-r from-cyan-600/10 via-blue-600/10 to-slate-700/10 border border-cyan-500/20 rounded-lg p-6 sm:p-8 text-center">
          <h3 className="text-xl sm:text-2xl font-semibold text-white mb-2">
            Ready to start earning?
          </h3>
          <p className="text-slate-300 text-sm sm:text-base mb-4">
            Every purchase with our partners brings you closer to greater
            rewards
          </p>
          <button className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg transition-all duration-200 text-sm sm:text-base">
            Explore All Offers
          </button>
        </div>
      </div>
    </div>
  );
}
